import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { StockCondition } from "../types/stock.types";
import type { UseFormReturn as UseFormReturnType } from "react-hook-form";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { memo, useState, useMemo } from "react";
import { PhotoUpload } from "@/shared/components/common/PhotoUpload";
import { getStockConditionLabel } from "../utils/stock.utils";

interface StockForm {
  productId: number;
  barcode: string;
  size: string;
  condition: StockCondition;
  photo?: string;
}

interface StockFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  loading: boolean;
  photoPreview: string | null;
  form: UseFormReturnType<StockForm>;
  products: ProductResponseDTO[];
  onClose: () => void;
  onSubmit: (data: StockForm) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemovePhoto: () => void;
}

export const StockFormDialog = memo(function StockFormDialog({
  open,
  mode,
  loading,
  photoPreview,
  form,
  products,
  onClose,
  onSubmit,
  onFileChange,
  onRemovePhoto,
}: StockFormDialogProps) {
  const [productFilter, setProductFilter] = useState("");
  const [isProductSelectOpen, setIsProductSelectOpen] = useState(false);

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          product.id.toString().includes(productFilter.toLowerCase()) ||
          product.brand.toLowerCase().includes(productFilter.toLowerCase()) ||
          product.name?.toLowerCase().includes(productFilter.toLowerCase()) ||
          product.color.toLowerCase().includes(productFilter.toLowerCase()),
      ),
    [products, productFilter],
  );

  const productId = form.watch("productId");
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId],
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Item de Estoque" : "Editar Item"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Adicione um novo item ao estoque"
              : "Atualize as informações do item"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="productId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Produto *</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Digite ID ou nome do produto..."
                          value={
                            isProductSelectOpen
                              ? productFilter
                              : selectedProduct
                                ? `#${selectedProduct.id} - ${selectedProduct.brand} - ${selectedProduct.name || selectedProduct.color}`
                                : ""
                          }
                          onChange={(e) => {
                            setProductFilter(e.target.value);
                            setIsProductSelectOpen(true);
                          }}
                          onClick={() => {
                            setIsProductSelectOpen(true);
                            setProductFilter("");
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setIsProductSelectOpen(false);
                              setProductFilter("");
                            }, 200);
                          }}
                          className="w-full"
                        />
                        {isProductSelectOpen && (
                          <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border bg-popover shadow-md">
                            {filteredProducts.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">
                                Nenhum produto encontrado
                              </div>
                            ) : (
                              filteredProducts.map((product) => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => {
                                    field.onChange(product.id);
                                    setIsProductSelectOpen(false);
                                    setProductFilter("");
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                >
                                  #{product.id} - {product.brand} -{" "}
                                  {product.name || product.color}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="barcode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código de Barras *</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o código de barras" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tamanho *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: 42, M, G" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="condition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condição *</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {Object.values(StockCondition).map((condition) => (
                        <option key={condition} value={condition}>
                          {getStockConditionLabel(condition)}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="photo"
              render={() => (
                <PhotoUpload
                  label="Foto do Item"
                  photoPreview={photoPreview}
                  onFileChange={onFileChange}
                  onRemovePhoto={onRemovePhoto}
                  inputId="stock-photo-upload"
                />
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : mode === "create"
                    ? "Criar Item"
                    : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
